import type { API } from "@grafana/faro-web-sdk";
import { Attributes, ObservabilityProvider } from "../provider";

// Set by initializeFaroRum. Held here instead of importing `faro` so the SDK stays out of the
// main bundle until Faro is enabled.
let faroApi: API | undefined;

// Calls made while the Faro chunk is loading. Undefined when no load is pending, so nothing is
// buffered when Faro is disabled.
type PendingCall = (api: API) => void;
let pendingCalls: PendingCall[] | undefined;

// Upper bound on buffered calls if the chunk is slow; later calls are dropped.
export const MAX_PENDING_CALLS = 100;

/** Starts buffering calls until setFaroApi provides the API (or clears the load with undefined). */
export const expectFaroApi = () => {
  if (!faroApi) {
    pendingCalls ??= [];
  }
};

/** Sets the API and replays buffered calls in order. undefined drops any buffered calls. */
export const setFaroApi = (api: API | undefined) => {
  faroApi = api;
  const calls = pendingCalls ?? [];
  pendingCalls = undefined;
  if (api) {
    calls.forEach((call) => call(api));
  }
};

const withApi = (call: PendingCall) => {
  if (faroApi) {
    call(faroApi);
  } else if (pendingCalls && pendingCalls.length < MAX_PENDING_CALLS) {
    pendingCalls.push(call);
  }
};

// Faro accepts string attribute values only.
const toFaroAttributes = (attributes: Attributes): Record<string, string> =>
  Object.fromEntries(Object.entries(attributes).map(([key, value]) => [key, String(value)]));

export const faroProvider: ObservabilityProvider = {
  event: (name, attributes) => {
    const faroAttributes = toFaroAttributes(attributes);
    // Keep the original time if the event is replayed after the chunk loads.
    const timestampOverwriteMs = Date.now();
    withApi((api) => api.pushEvent(name, faroAttributes, undefined, { timestampOverwriteMs }));
  },
  identify: (userId, attributes) => {
    const faroAttributes = toFaroAttributes(attributes);
    withApi((api) =>
      api.setUser({
        ...(userId && { id: userId }),
        attributes: faroAttributes,
      })
    );
  },
  reset: () => {
    withApi((api) => api.resetUser());
  },
};
